import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  IsServiceTypeVendor,
  replaceVideoUrl,
  tokenDecoder,
  useQuery,
} from "../../lib/utils";
import {
  useGetStripeAccountLinkQuery,
  useGetVendorDetailsQuery,
  useUpdateVendorDetailsMutation,
} from "../../api/api";
import { useToast } from "../../@/components/ui/use-toast";
import Loader from "../../components/LoaderComponent";
import { ChevronRight } from "lucide-react";
import { SubmitHandler, useForm } from "react-hook-form";
import { StoreInformation } from "./StoreInformation";
import LocationSettings from "./LocationSettings";
import PaymentDetails from "./PaymentDetails";
import AccountManagement from "./AccountManagement";
import ShareButton from "../../components/ShareButton";
import { IoMdShare } from "react-icons/io";
import { DEEP_LINK_BASE_URL, USER_ROLE } from "../../lib/constants";
import CouponTagsConfiguration from "../AdminConfigurationPage/CouponTagsConfiguration";
import AvailabilityManager from "../../components/Availability/AvailabilityManager";
import { MdOutlineMenu } from "react-icons/md";
import { HiOutlineX } from "react-icons/hi";
import { isMobileScreen } from "../../lib/utils";
import PayoutWarning from "./PayoutWarning";

interface Tag {
  id: number;
  name: string;
  tagId?: number;
  vendorId?: number;
}

interface RemovedTags {
  id: number;
}

interface StoreInfo {
  vendorImage: string;
  vendorImageType: string;
  vendorImageBlob: string;
  themeColor: string;
  serviceTypeId: number;
  company: string;
  storeTags?: Tag[];
  removedTags?: RemovedTags[];
  website: string;
  aboutStore?: string;
  returnInfo?: string;
  tagLine?: string;
  isCatering: boolean;
}

interface LocationInfo {
  longitude: string;
  latitude: string;
  standardDeliveryFee?: number | null;
  address: string;
  cityName: string;
  state: string;
  zipCode: string;
}

interface PaymentInfo {
  SSN?: string;
  merchantCode?: string;
  accountHolderName?: string;
  bankName?: string;
  accountNumber?: string;
  transitNumber?: string; // Added this line
  institutionNumber?: string; // Added this line
  swiftCode?: string;
  dob?: string;
}

interface ContactInfo {
  firstName: string;
  lastName: string;
  phone: number;
  countryCode: string;
  email: string;
}

interface StoreFormInputs {
  storeInfo: StoreInfo;
  locationInfo: LocationInfo;
  paymentInfo: PaymentInfo;
  contactInfo: ContactInfo;
}

interface LayoutContext {
  setShowAlertOnRouteChange: React.Dispatch<React.SetStateAction<boolean>>;
  showAlertOnRouteChange: boolean;
}

const StorePage: React.FC = () => {
  const { setShowAlertOnRouteChange, showAlertOnRouteChange } =
    useOutletContext<LayoutContext>();
  const [isDisabled, setIsDisabled] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialValues, setInitialValues] = useState<any>({});
  const navigate = useNavigate();
  const token = useSelector((state: any) => state.auth.token);
  const decodedToken = tokenDecoder(token);
  const query = useQuery();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const routeName = useSelector(
    (state: any) => state.appstate?.routeName || "defaultRouteName"
  );

  const vendorId =
    decodedToken.role === "ADMIN"
      ? parseInt(query.get("vendorId") || "0")
      : decodedToken.role === "VENDOR"
      ? decodedToken.vendorId
      : 0;

  const {
    data: profileData,
    isLoading: profileLoading,
    ...getVendor
  } = useGetVendorDetailsQuery(
    {
      vendorId: vendorId,
    },
    { refetchOnMountOrArgChange: true }
  );
  let vendorIdFromProfileData = profileData?.data?.id || 0;
  const [UpdateVendorDetails] = useUpdateVendorDetailsMutation();
  const { data: stripeUrl } = useGetStripeAccountLinkQuery(
    { vendorId: vendorIdFromProfileData },
    {
      skip:
        profileData?.data?.id === undefined ||
        profileData?.data?.id === null ||
        vendorIdFromProfileData === 0,
    }
  );
  const { toast } = useToast();
  const [removedTags, setRemovedTags] = useState<RemovedTags[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [vendorTags, setVendorTags] = useState<any>([]);
  const [selectedButtonIndex, setSelectedButtonIndex] = useState(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [timeSlotData, setTimeSlotData] = useState({});
  const [isInitialTabSet, setIsInitialTabSet] = useState(false);

  const isServiceTypeVendor = IsServiceTypeVendor();
  const [redirectToStripe, setRedirectToStripe] = useState(false);

  const formStepOptions = [
    {
      title: "Store Information",
      id: 1,
    },
    {
      title: "Availability Management",
      id: 2,
    },
    {
      title: "Location Settings",
      id: 3,
    },
    {
      title: "Payment & Payout Details",
      id: 4,
    },
    {
      title: "Account Management",
      id: 5,
    },
    {
      title: "Coupons Management",
      id: 6,
    },
  ].filter((option) => isServiceTypeVendor || option.id !== 2);

  // Function to get query parameters
  const getQueryParams = (param: string) => {
    const params = new URLSearchParams(location.search);
    return params.get(param);
  };

  // Get the selectedTab value from query parameters
  const selectedTab = getQueryParams("slectedTab");
  const vendorIdFromQuery = getQueryParams("vendorId");

  const handlePhoneChange = (code: string, number: string) => {
    setSelectedCode(code);
    setPhoneNumber(number);
    setValue("phone", number);
    setValue("countryCode", code);
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<any>();

  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      if (type === "change") {
        setShowAlertOnRouteChange(true);
      } else if (type === "blur") {
        setShowAlertOnRouteChange(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const [selectedCode, setSelectedCode] = useState(watch("countryCode"));
  const [phoneNumber, setPhoneNumber] = useState(watch("phone"));

  // Watch all form fields
  const formValues = watch();

  const onSubmit: SubmitHandler<StoreFormInputs> = async (data) => {
    // console.log("showAlertOnRouteChange bedore", showAlertOnRouteChange);
    setShowAlertOnRouteChange(false);
    try {
      let routingNumber =
        formValues?.transitNumber && formValues?.institutionNumber
          ? `${formValues.transitNumber}-${formValues.institutionNumber}`
          : undefined;
      const updatedData: any = {
        ...data,
        routingNumber: routingNumber,
        institutionNumber: undefined,
        transitNumber: undefined,
      };
      setIsDisabled(true);
      const newTags = selectedTags.filter(
        (tag) =>
          !vendorTags?.some((fStateTag: any) => fStateTag.tagId === tag.id)
      );
      const formStatewithId = {
        ...updatedData,
        ...timeSlotData,
        id: vendorId,
        vendorTags: newTags.map((tag) => ({
          tagId: tag.id,
          vendorId: vendorId,
        })),
        removedTags: removedTags.length > 0 ? removedTags : undefined,
      };

      const response = await UpdateVendorDetails({
        vendorId: vendorId,
        editVendorDetailDto: formStatewithId,
      }).unwrap();

      setIsDisabled(false);
      getVendor.refetch();
      setShowAlertOnRouteChange(false);
      // console.log("showAlertOnRouteChange after", showAlertOnRouteChange);
      if (
        !response.data?.isEligibleForPayout &&
        response.data?.stripeAccountId
      ) {
        setRedirectToStripe(true);
        toast({
          title: "Some information is missing",
          description: `Now you will redirect to the stripe account page to complete the information`,
        });

        if (stripeUrl?.data?.url) {
          setTimeout(() => {
            window.location.href = stripeUrl?.data?.url || "";
          }, 3000);
        }

        setRemovedTags([]);
        setHasUnsavedChanges(false);
        return;
      }

      toast({
        title: "Success",
        description: `${response?.message || "Vendor updated successfully"}`,
      });
      setRemovedTags([]);
      setHasUnsavedChanges(false);
    } catch (error: any) {
      setIsDisabled(false);
      toast({
        title: "Error",
        description: `${error?.message || "Failed to update vendor"}`,
      });
    }
  };

  useEffect(() => {
    if (stripeUrl?.data?.url && redirectToStripe) {
      setTimeout(() => {
        window.location.href = stripeUrl?.data?.url || "";
      }, 3000);
    }
  }, [redirectToStripe, stripeUrl]);

  const handleFormatPayload = (
    timeSlotsPayload: any,
    adjustedBlockedDates: any,
    removedTimeSlots: any,
    unavailableSlotsIds: any
  ) => {
    const payload: any = {
      removedTimeSlotIds:
        removedTimeSlots.length > 0 || unavailableSlotsIds.length > 0
          ? [...removedTimeSlots, ...unavailableSlotsIds]
          : undefined,
      timeSlots: timeSlotsPayload.length > 0 ? timeSlotsPayload : undefined,
      blockDate:
        adjustedBlockedDates.length > 0 ? adjustedBlockedDates : undefined,
    };

    setTimeSlotData(payload);
  };

  useEffect(() => {
    // Skip if we've already set the initial tab
    if (isInitialTabSet) {
      // Only update URL when tab changes after initial set
      const params = new URLSearchParams(location.search);
      params.set("tab", selectedButtonIndex.toString());

      // Preserve existing vendorId if present
      const vendorId = params.get("vendorId");
      if (vendorId) {
        params.set("vendorId", vendorId);
      }

      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
      return;
    }

    // Initial tab setting
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get("tab");

    if (tabFromUrl) {
      const tabIndex = parseInt(tabFromUrl);
      if (tabIndex >= 1 && tabIndex <= formStepOptions.length) {
        setSelectedButtonIndex(tabIndex);
      }
    } else if (selectedTab === "2") {
      setSelectedButtonIndex(2);
    } else {
      setSelectedButtonIndex(1);
    }

    setIsInitialTabSet(true);
  }, [location.search, selectedTab, selectedButtonIndex, isInitialTabSet]);

  useEffect(() => {
    if (profileLoading === false && profileData) {
      const routingNumber = profileData?.data?.routingNumber || "";
      let transitNumber = "";
      let institutionNumber = "";

      if (routingNumber.includes("-")) {
        [transitNumber, institutionNumber] = routingNumber.split("-");
      } else if (routingNumber.length === 8) {
        transitNumber = routingNumber.slice(0, 5);
        institutionNumber = routingNumber.slice(5);
      } else {
        transitNumber = routingNumber.slice(0, 5);
      }

      const initialData = {
        company: profileData?.data?.company || "",
        website: profileData?.data?.website || "",
        aboutStore: profileData?.data?.aboutStore || "",
        returnInfo: profileData?.data?.returnInfo || "",
        tagLine: profileData?.data?.tagLine || "",
        standardDeliveryFee: profileData?.data?.standardDeliveryFee || 29.99,
        SSN: profileData?.data?.SSN || "",
        merchantCode: profileData?.data?.merchantCode || "",
        accountHolderName: profileData?.data?.accountHolderName || "",
        bankName: profileData?.data?.bankName || "",
        accountNumber: profileData?.data?.accountNumber || "",
        transitNumber, // Set extracted transitNumber
        institutionNumber, // Set extracted institutionNumber
        swiftCode: profileData?.data?.swiftCode || "",
        firstName: profileData?.data?.firstName || "",
        lastName: profileData?.data?.lastName || "",
        email: profileData?.data?.email || "",
        phone: profileData?.data?.phone || "",
        countryCode: profileData?.data?.countryCode || "",
        address: profileData?.data?.address || "",
        cityName: profileData?.data?.cityName || "",
        state: profileData?.data?.state || "",
        zipCode: profileData?.data?.zipCode || "",
        themeColor: profileData?.data?.themeColor || "",
        serviceTypeId: profileData?.data?.serviceTypeId || 0,
        vendorImage: replaceVideoUrl(profileData?.data?.vendorImage || ""),
        latitude: profileData?.data?.latitude || 0,
        longitude: profileData?.data?.longitude || 0,
        vendorTags: profileData?.data?.vendorTags || [],
        numberOfStores: profileData?.data?.numberOfStores || 0,
        vendorImageBlob: "",
        wallpaperImageUrl: replaceVideoUrl(
          profileData?.data?.wallpaperImageUrl || ""
        ),
        DOB: profileData?.data?.DOB || "",
        vendorImageType: "",
        wallpaperImageType: "",
        wallpaperImageBlob: "",
      };

      reset(initialData);
      setInitialValues(initialData);
      setVendorTags(profileData?.data?.vendorTags || []);
    }
  }, [profileData, profileLoading]);

  // Prevent form submission if there are unsaved changes in reload
  // useEffect(() => {
  //   const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  //     if (hasUnsavedChanges) {
  //       event.preventDefault();
  //       event.returnValue = ""; // Required for Chrome to show the warning dialog
  //     }
  //   };

  //   window.addEventListener("beforeunload", handleBeforeUnload);

  //   return () => {
  //     window.removeEventListener("beforeunload", handleBeforeUnload);
  //   };
  // }, [hasUnsavedChanges]);

  useEffect(() => {
    // Deep comparison of form values
    const isFormDirty =
      JSON.stringify(formValues) !== JSON.stringify(initialValues);
    setHasUnsavedChanges(isFormDirty);
  }, [formValues, initialValues]);

  const handleTabClick = (tabId: number) => {
    setSelectedButtonIndex(tabId);

    if (isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  };

  // Update the sidebarClasses string
  const sidebarClasses = `col-span-2 px-4 pb-6 pt-2 min-w-52 max-[1523px]:fixed max-[1523px]:left-0 max-[1523px]:top-0 max-[1523px]:bg-white max-[1523px]:h-screen max-[1523px]:overflow-y-auto max-[1523px]:z-50 max-[1523px]:shadow-lg ${
    isSidebarOpen
      ? "max-[1523px]:translate-x-0"
      : "max-[1523px]:-translate-x-full"
  } transition-transform duration-300 ease-in-out`;

  // Add new useEffect to handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only handle clicks when sidebar is open and screen width is <= 1523px
      if (isSidebarOpen && window.innerWidth <= 1523) {
        const sidebar = document.getElementById("store-sidebar");
        const menuButton = document.getElementById("menu-button");

        // Close sidebar if click is outside sidebar and not on menu button
        if (
          sidebar &&
          menuButton &&
          !sidebar.contains(event.target as Node) &&
          !menuButton.contains(event.target as Node)
        ) {
          setIsSidebarOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSidebarOpen]);

  return (
    <div>
      <form
        onSubmit={(e) => {
          // Prevent form submission if on Coupons page
          if (selectedButtonIndex === 6 || selectedButtonIndex === 2) {
            e.preventDefault();
            return;
          }
          handleSubmit(onSubmit)(e);
        }}
        className="bg-bgColor max-h-screen"
      >
        <div className="flex justify-between bg-customOffWhiteColor px-6 py-2">
          <div className="flex gap-2">
            <button
              id="menu-button"
              type="button"
              className="hidden max-[1523px]:block"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              <MdOutlineMenu className="w-6 h-6" />
            </button>
            <button
              className="flex text-sm font-normal items-center justify-center bg-customGreenColor text-white px-4 py-2 rounded-lg"
              type="button"
              disabled={isDisabled || selectedButtonIndex === 6}
              onClick={(e) => {
                if (selectedButtonIndex === 6) {
                  e.preventDefault();
                  return;
                }
                handleSubmit(onSubmit)(e);
              }}
            >
              {!isDisabled && (
                <img
                  src="/Images/ProfileFormImages/document-text.png"
                  className="w-5 h-5 mr-1"
                  alt="Save"
                />
              )}
              {isDisabled ? "Saving..." : "Save"}
            </button>
            <button
              className="flex text-sm font-normal items-center justify-center bg-transparent text-customGrayText border border-customGrayText px-4 py-2 rounded-lg"
              type="button"
              disabled={isDisabled}
              onClick={() => {
                setShowAlertOnRouteChange(false);
                if (decodedToken.role === "ADMIN") {
                  navigate("/vendors");
                } else {
                  navigate("/dashboard");
                }
                setHasUnsavedChanges(false); // Reset unsaved changes state on cancel
              }}
            >
              <img
                src="/Images/ProfileFormImages/close-square.png"
                className="w-5 h-5 mr-1"
                alt="Close"
              />
              {decodedToken.role === "ADMIN" ? "Back" : "Cancel"}
            </button>
          </div>
          <div className="flex items-center gap-2">
            {profileData?.data && (
              <>
                {/* // show good error message if vendorId is not isEligibleForPayout */}
                {profileData?.data?.isEligibleForPayout === false &&
                  profileData?.data?.stripeAccountId && (
                    <PayoutWarning vendorId={profileData.data.id} />
                  )}
                <button
                  className="flex text-sm font-normal items-center justify-center bg-customGreenColor text-white px-4 py-2 rounded-lg"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsDialogOpen(true);
                  }}
                >
                  <IoMdShare className="w-5 h-5 mr-1" />
                  Share
                </button>

                <ShareButton
                  setIsDialogOpen={setIsDialogOpen}
                  isDialogOpen={isDialogOpen}
                  text={`Share your store with friends and customers! Copy the link below and send it to them via email, chat, or social media to showcase your products and grow your business.`}
                  title={`Share Your Store Link`}
                  url={`${DEEP_LINK_BASE_URL}/Main/HomeStack/RedirectToStoreContainer?id=${vendorId}`}
                />
              </>
            )}
          </div>
        </div>

        <div
          className={`flex border-t w-full relative  ${
            isMobileScreen ? "" : "pl-6 pr-2"
          }`}
        >
          <div className={sidebarClasses} id="store-sidebar">
            <div className="flex justify-end">
              <button
                type="button"
                className="hidden max-[1523px]:block pl-2"
                onClick={() => setIsSidebarOpen(false)}
              >
                <HiOutlineX className="w-6 h-6 text-customGray" />
              </button>
            </div>
            <h1 className="text-xl font-semibold leading-8 text-customBlackColor">
              Store Configuration
            </h1>
            <div className="mb-2 text-xs font-normal leading-5 text-customBlackColor">
              Customize Your Store Details
            </div>
            <div className="w-full">
              {formStepOptions.map((option) => (
                <div
                  key={option.id}
                  className="flex items-center justify-between py-3"
                >
                  <span
                    className={`cursor-pointer flex justify-between w-full text-sm font-normal ${
                      selectedButtonIndex === option.id
                        ? "text-customGreenColor font-helveticaMedium"
                        : "text-customGrayDark"
                    }`}
                    onClick={() => handleTabClick(option.id)}
                  >
                    <span className="text-left">{option.title}</span>
                    <ChevronRight
                      className="mt-[0.2rem] w-4 h-4"
                      color={
                        selectedButtonIndex === option.id
                          ? "#008000"
                          : "#BEC0CA"
                      }
                    />
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2 w-full px-4">
            {selectedButtonIndex === 1 ? (
              <StoreInformation
                register={register}
                errors={errors}
                clearErrors={clearErrors}
                removedTags={removedTags}
                selectedTags={selectedTags}
                setRemovedTags={setRemovedTags}
                setSelectedTags={setSelectedTags}
                vendorTags={vendorTags}
                watch={watch}
                setValue={setValue}
                setShowAlertOnRouteChange={setShowAlertOnRouteChange}
              />
            ) : selectedButtonIndex === 2 ? (
              <AvailabilityManager
                vendorIdFromProp={vendorIdFromQuery}
                isForProduct={false}
                isEditingProduct={false}
                handleFormatPayload={handleFormatPayload}
                isShowSaveButton={
                  decodedToken.role === USER_ROLE.VENDOR ? true : false
                }
                isPreviewOnly={
                  decodedToken.role === USER_ROLE.VENDOR ? false : true
                }
              />
            ) : selectedButtonIndex === 3 ? (
              <LocationSettings
                register={register}
                errors={errors}
                clearErrors={clearErrors}
                setValue={setValue}
                watch={watch}
                setShowAlertOnRouteChange={setShowAlertOnRouteChange}
              />
            ) : selectedButtonIndex === 4 ? (
              <PaymentDetails
                register={register}
                errors={errors}
                watch={watch}
                setValue={setValue}
                setShowAlertOnRouteChange={setShowAlertOnRouteChange}
              />
            ) : selectedButtonIndex === 5 ? (
              <AccountManagement
                register={register}
                errors={errors}
                setValue={setValue}
                clearErrors={clearErrors}
                setSelectedCode={setSelectedCode}
                selectedCode={selectedCode}
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
                onPhoneChange={handlePhoneChange}
                watch={watch}
                setShowAlertOnRouteChange={setShowAlertOnRouteChange}
              />
            ) : (
              <CouponTagsConfiguration />
            )}
          </div>
        </div>
      </form>
      {profileLoading && <Loader loading={profileLoading} />}
    </div>
  );
};

export default StorePage;
